import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { UsuarioTipo } from '../models/usuario-tipo.enum';
import { environment } from '@sharedEnvironment/environment';
import { LoginResponse } from '../models/loginResponse.interface';
import {
  AbonoQr,
  AppAbonado,
  GetUserResponse,
  ResultResponse,
  UserUpdateRequest,
  UserUpdateRequestToDatosPersonales,
} from '../models/user.interface';
import { CookieService } from 'ngx-cookie-service';
//import { NotificationService } from '@lib/services/notification.service';
import { Router } from '@angular/router';
import { LastPayment } from '@lib/models/lastPayment.interface';
import { SharedDataService } from '@lib/services/shared-data.service';
import { CamisetaRenovacionResponse } from '@lib/models/camiseta-renovacion';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + environment.tokenAPI,
  }),
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  //*Método para inyectar headers

  //*Propiedad para trabajar en LOCAL con el SSO: Modificar a TRUE para pasar guards
  readonly environmentLocal: boolean = false;

  //* Observable para almacenar estado de user
  userSubject: BehaviorSubject<LoginResponse | null> =
    new BehaviorSubject<LoginResponse | null>(null);

  private getQrCache: { data: AbonoQr; at: number } | null = null;
  private readonly GET_QR_TTL_MS = 60_000;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private shared: SharedDataService,
    //private notificationService:NotificationService,
    private router: Router
  ) { }

  listPais(): Observable<any> {
    return this.http.get(environment.backAPI + '/listPais', httpOptions);
  }

  listMunicipioCp(cp: string): Observable<any> {
    return this.http.post(
      environment.backAPI + '/listMunicipioCp',
      {
        cp,
      },
      httpOptions
    );
  }

  listProvincia(): Observable<any> {
    return this.http.get(environment.backAPI + '/listProvincia', httpOptions);
  }

  listProvinciaWithToken(token: any): Observable<any> {
    return this.http.get(environment.backAPI + '/listProvincia',
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        }
      });
  }

  listPaisWithToken(token: any): Observable<any> {
    return this.http.get(environment.backAPI + '/listPais',
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        }
      });
  }

  listMunicipioCpWithToken(cp: string, token: any): Observable<any> {
    return this.http.post(
      environment.backAPI + '/listMunicipioCp',
      {
        cp,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        }
      }
    );
  }

  getPriceAvet(idRenovation: string | null, token: any): Observable<any> {
    return this.http.get(
      environment.backAPI + `/events/renovation/getPriceAvet/${idRenovation}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        }
      }
    );
  }

  //*Método para tomar el token desde el loginReponse e inyectarlo comp HTTP headers a get users

  //*Modificación para manejo de interfaz y captura dinámica de valores de loginResponse

  public getUser(): Observable<GetUserResponse> {
    const currentUser = this.userSubject.value;

    if (currentUser && currentUser.id) {
      const url = `${environment.backAPI}/users/get/${currentUser.id}`;
      return this.http.get<GetUserResponse>(url, httpOptions);
    } else {
      return throwError('No hay usuario actual o falta el ID');
    }
  }

  // public getUserLocalStorage(): Observable<GetUserResponse> {
  //   const userDataString = localStorage.getItem('userData');
  //   if (userDataString) {
  //     const currentUser = JSON.parse(userDataString);

  //     if (currentUser && currentUser.id) {
  //       console.log("CURRENT USER LOCAL STORAGE EN SERVICIO:", currentUser)
  //       const url = `${environment.backAPI}/users/get/${currentUser.id}`;
  //       return this.http.get<GetUserResponse>(url, httpOptions);
  //     } else {
  //       return throwError(
  //         () => new Error('No hay usuario actual o falta el ID')
  //       );
  //     }
  //   } else {
  //     return throwError(() => new Error('No hay usuario actual o falta el ID'));
  //   }
  // }

  public getUserLocalStorage(): Observable<GetUserResponse> {
    const userDataString = localStorage.getItem('RBB-userData');
    if (userDataString) {
      const currentUser = JSON.parse(userDataString);
      if (currentUser && currentUser.id) {
        if (environment.useMockedData) {
          const url = `assets/mock-data/mockGetUserResponse.json`;
          return this.http.get<GetUserResponse>(url);
        }
        const url = `${environment.backAPI}/users/get/${currentUser.id}`;
        return this.http.get<GetUserResponse>(url, httpOptions).pipe(
          catchError((error) => {
            console.error('Error al obtener datos del usuario:', error);
            return throwError(() => new Error('Error en la petición HTTP'));
          })
        );
      } else {
        return throwError(
          () => new Error('No hay usuario actual o falta el ID')
        );
      }
    } else {
      return throwError(() => new Error('No hay usuario actual o falta el ID'));
    }
  }

  public getUserSSO(
    userId: number,
    httpOptions: { headers: HttpHeaders }
  ): Observable<GetUserResponse> {
    if (environment.useMockedData) {
      const url = `assets/mock-data/mockGetUserResponse.json`;
      return this.http.get<GetUserResponse>(url);
    }
    const url = `${environment.backAPI}/users/get/${userId}`;
    return this.http.get<GetUserResponse>(url, httpOptions);
  }

  getDetailsSuscriptions(idAbonado: number): Observable<any> {
    return this.http.get(
      environment.backAPI +
      `/application/get/suscriptions/abonado/${idAbonado}`,
      httpOptions
    );
  }

  putCancelSuscription(id: string): Observable<any> {
    return this.http.put(
      environment.backAPI + '/application/suscriptions/cancel',
      {
        id,
      },
      httpOptions
    );
  }

  updateUser(userData: UserUpdateRequest): Observable<any> {
    return this.http.post(
      environment.backAPI + '/users/update',
      userData,
      httpOptions
    );
  }

  updateConsents(userData: UserUpdateRequest): Observable<any> {
    return this.http.post(
      environment.backAPI + '/users/update/consents',
      userData,
      httpOptions
    );
  }

  updateUserDatosPersonales(
    userData: UserUpdateRequestToDatosPersonales
  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/users/update',
      userData,
      httpOptions
    );
  }

  /*updateFanDatosPersonales(
    userData: FanRequest
  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/fan/update-fan',
      userData,
      httpOptions
    );
  }*/

  listMunicipioIdProvcincia(provinciaId: string): Observable<any> {
    return this.http.post(
      environment.backAPI + '/listMunicipioIdProvcincia',
      {
        provinciaId,
      },
      httpOptions
    );
  }

  listMunicipioIdProvcinciaWithToken(provinciaId: string, token: any): Observable<any> {
    return this.http.post(
      environment.backAPI + '/listMunicipioIdProvcincia',
      {
        provinciaId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        }
      }
    );
  }

  saveFanAsUser(type: string, id_betis: string): Observable<any> {
    const url = `${environment.backAPI}/fan/save-fan-as-user/${type}/${id_betis}`;
    return this.http.get(url, httpOptions);
  }

  completeRegister(formularioDeDatos: FormData): Observable<any> {
    return this.http.post(
      environment.backAPI + '/fan/update-fan',
      formularioDeDatos,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${environment.tokenAPI}`,
        }),
      }
    );
  }

  altaSoyBeticoSocio(
    idUser: string
  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/users/alta-soy-betico',
      {
        idUser
      },
      httpOptions
    );
  }

  altaSoyPalmerin(
    idUser: string
  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/users/alta-soy-palmerin',
      {
        idUser
      },
      httpOptions
    );
  }

  getPayURL(
    customerId: number,
    applicationId: number,
    forceTokenRequest: boolean
  ): Observable<any> {
    // TODO: añadimos el valor para asegurarnos que es un valor que no existe y no coincide con idabonado ni id
    // Cuando se decida la solución final habrá que borrarlo o adaptarlo. (ASB)
    //customerId = customerId + 90000000
    return this.http.post(
      environment.backAPI + '/tpv/get-payment-url',
      { customerId, applicationId, forceTokenRequest },
      httpOptions
    );
  }

  getPayInfo(applicationId: number, token: string): Observable<any> {
    return this.http.get(
      environment.backAPI + `/tpv/get-payment-info/${applicationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        },
      }
    );
  }

  revocarCesionActivacion(idActivacion: number, token: string): Observable<any> {
    return this.http.post(
      environment.backAPI + `/serviciosAbonado/activacion/revocar-cesion/${idActivacion}/${token}`, null,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        },
      });
  }

  getActivationPayInfo(id: number, token: string, ids?: number[]): Observable<any> {
    let params = new HttpParams();

    if (ids && ids.length > 0) {
      ids.forEach(id => {
        params = params.append('ids', id.toString());
      });
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      Tokenizada: 'si',
    });

    return this.http.get(
      environment.backAPI + `/tpv/activation/get-payment-info/${id}/${token}`,
      { params, headers }
    );
  }

  getRenovation(id: number, token: string): Observable<any> {
    return this.http.get(environment.backAPI + `/events/renovation/get/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Tokenizada: 'si',
      },
    });
  }

  checkVoucher(codeVoucher: string, idApplication: number): Observable<any> {
    return this.http.post(
      environment.backAPI + '/voucher/valid',
      { codeVoucher, idApplication },
      httpOptions
    );
  }

  deleteUseVoucher(
    codeVoucher: string,
    idApplication: number
  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/voucher/delete-use',
      { codeVoucher, idApplication },
      httpOptions
    );
  }

  singinNewsletter(key: string): Observable<any> {
    return this.http.post(
      environment.backAPI + '/newsletter-sb',
      { key },
      httpOptions
    );
  }

  getPalmerin(idApplication: number, token: string): Observable<any> {
    return this.http.get(
      environment.backAPI + `/application/get/palmerin/${idApplication}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        },
      }
    );
  }

  subsanarPalmerin(
    formularioDeDatos: FormData,
    token: string
  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/users/subsanar-palmerin',
      formularioDeDatos,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
          Tokenizada: 'si',
        }),
      }
    );
  }

  //* Comprobación de cookie por nombre
  private getSSOCookie(name: string): string | null {
    return this.cookieService.get(name);
  }

  //* Método que retorna booleano para comprobar cookie SSO y usar en guard
  isLoggedIn(): boolean {
    return !!this.getSSOCookie('SSO-TEL');
  }


  // isLoggedIn(): boolean {
  //   return !!localStorage.getItem('RBB-userData');
  // }



  //*------Método de login, añadido parámetro para trabajar en local--------
  public login(): Observable<LoginResponse | null> {
    if (this.isLoggedIn()) {
      if (environment.debug) {
        return this.loginMock().pipe(
          tap((response: LoginResponse) => {
            this.userSubject.next(response);
            this.handleLoginResponse(response);
          })
        );
      } else {
        const ssoCookieContent = this.getSSOCookie('SSO-TEL');
        if (ssoCookieContent) {
          const body = { cookie: ssoCookieContent };
          return this.http.post<LoginResponse>(`${environment.backAPI}/signin/sso`, body).pipe(
            tap(response => {
              this.userSubject.next(response);
              this.handleLoginResponse(response);
            })
          );
        }
      }
    }
    return of(null);
  }

  public async loginFan(membresia: string): Promise<Observable<LoginResponse | null>> {
    if (this.isLoggedIn()) {
      const ssoCookieContent = this.getSSOCookie('SSO-TEL');
      if (ssoCookieContent) {
        const body = { cookie: ssoCookieContent };
        await this.http.post<LoginResponse>(`${environment.backAPI}/signin/fan`, body).subscribe(response => {
          this.userSubject.next(response);
          this.handleLoginResponse(response)
          let userDataString = localStorage.getItem('RBB-userData');
          while (!userDataString) {
            userDataString = localStorage.getItem('RBB-userData');
          }
          this.handleFanLogin(membresia, response);
        });
      }
    }
    return of(null);
  }

  handleFanLogin(membresia: string, response: LoginResponse) {
    if (membresia === "") {
      window.location.replace('/socios/hazte-socio');
    } else {
      if (membresia === "shop") {
        window.location.href = environment.shopURL;
      } else {
        window.location.replace('/socios/hazte-socio/' + membresia + '/pago');
      }
    }
  }


  handleLoginResponse(response: LoginResponse) {
    if (response.id) {
      this.clearUserTypeCache();
      this.clearHealthCaches();
      this.shared.removeData('appAbonadoRepresentado');
      this.userSubject.next(response);
      this.userStorageInLocal(response);
    }

  }

  private redirectToHome() {

    this.router.navigate(['socios']);
  }

  userStorageInLocal(userData: LoginResponse) {
    const userDataString = JSON.stringify({
      userName: userData.userName,
      id: userData.id,
      nombre: userData.nombre,
      token: userData.token,
      id_abonado: userData.id_abonado,
      id_betis: userData.id_betis,
      socio: userData.socio,
      roles: userData.roles,
      availableProduct: userData.availableProduct,
      pendingProducts: userData.pendingProducts,
      isCompany: userData.isCompany,
      isPenya: userData.roles.filter(rol => rol == "penya").length > 0,
      isFan: userData.roles.filter(rol => rol == "fan").length > 0,
      isAbonado: userData.roles.filter(rol => rol == "fan").length === 0 && userData.id_abonado != null && userData.id_abonado > 0
    });
    localStorage.setItem('RBB-userData', userDataString);
  }

  public getCurrentUserLocalStorage(): Observable<LoginResponse | null> {
    const userDataString = localStorage.getItem('RBB-userData');
    if (userDataString) {
      const userData: LoginResponse = JSON.parse(userDataString);
      return of(userData);
    } else {
      return of(null);
    }
  }

  //*Obtener el user como observable
  getCurrentUser(): Observable<LoginResponse | null> {
    return this.userSubject.asObservable();
  }

  //*Obtener el usar como value
  getCurrentUserValue(): LoginResponse | null {
    return this.userSubject.value;
  }

  loginMock(): Observable<LoginResponse> {

    const mockUrl = `assets/mock-data/mockLoginResponse.json`;
    return this.http.get<LoginResponse>(mockUrl).pipe(
      tap((mockResponse: LoginResponse) => {
        this.userSubject.next(mockResponse);
        this.handleLoginResponse(mockResponse);
      })
    );
  }

  //*Servicio para eliminar la cookie del SSO, deslogeando al user
  logOut(): void {

    // Eliminamos, si existe, los datos de sessionStorage
    this.cleanShared();


    // Creamos la url de logout del SSO
    const apiKeySSOLogout = environment.apiKeySSO.replace('/?', '/logout?');

    // Creamos la url de logout del SSO
    const logoutURL = apiKeySSOLogout + '&dynamicURL=' + environment.sociosURL;

    // Navegamos a la url del SSO para hacer logout
    window.location.href = logoutURL;

  }

  cleanShared() {
    this.clearHealthCaches();
    this.shared.removeData('representadoSeleccionadoId');
    this.shared.removeData('appAbonadoRepresentado');
    this.shared.removeData('appAbonado');
  }

  //*Método privado para eliminar cookie por nombre:usando librería cookieService de Angular
  private deleteCookie(name: string): void {
    let domain = '.realbetisbalompie.es';
    if (environment.debug) {
      domain = 'localhost';
    }
    //let domain = 'localhost';
    document.cookie =
      name +
      `=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
    this.cookieService.delete(name, '/', domain);
  }

  getUserBetico(id: number): Observable<any> {
    return this.http.get(
      `${environment.backAPI}/users/${id}/es-betico`
    );
  }

  getUserBeticoDigital(id: number): Observable<any> {
    return this.http.get(
      `${environment.backAPI}/users/${id}/es-betico-digital`
    );
  }

  private userTypeCache$: Observable<string> | null = null;

  /** Tipo de socio (SB, SBD, SBP, FAN, …) — GET /users/type (JWT del usuario en Authorization). */
  getUserType(): Observable<string> {
    if (this.userTypeCache$) {
      return this.userTypeCache$;
    }

    const userId = this.getStoredUserId();

    this.userTypeCache$ = this.http
      .get(`${environment.backAPI}/users/type`, { responseType: 'text' })
      .pipe(
        map((response) => this.normalizeUserTypeResponse(response)),
        catchError(() =>
          userId ? this.inferUsuarioTipo(userId) : of(UsuarioTipo.FAN)
        ),
        shareReplay(1)
      );

    return this.userTypeCache$;
  }

  clearUserTypeCache(): void {
    this.userTypeCache$ = null;
  }

  private getStoredUserId(): number | null {
    const userDataString = localStorage.getItem('RBB-userData');
    if (!userDataString) {
      return null;
    }
    try {
      const userData = JSON.parse(userDataString);
      return userData?.id ?? null;
    } catch {
      return null;
    }
  }

  private normalizeUserTypeResponse(response: unknown): string {
    if (typeof response === 'string') {
      return response.replace(/"/g, '').trim().toUpperCase();
    }

    if (response && typeof response === 'object') {
      const value =
        (response as { type?: string }).type ??
        (response as { data?: string }).data ??
        '';
      return String(value).replace(/"/g, '').trim().toUpperCase();
    }

    return UsuarioTipo.FAN;
  }

  private inferUsuarioTipo(userId: number): Observable<string> {
    return forkJoin({
      esBetico: this.getUserBetico(userId),
      esDigital: this.getUserBeticoDigital(userId),
    }).pipe(
      map(({ esBetico, esDigital }) => {
        if (esBetico !== true) {
          return UsuarioTipo.SB;
        }
        if (esDigital !== true) {
          return UsuarioTipo.SBD;
        }
        return UsuarioTipo.FAN;
      }),
      catchError(() => of(UsuarioTipo.FAN))
    );
  }

  getUserPalmerin(id: number): Observable<any> {
    return this.http.get(
      `${environment.backAPI}/users/${id}/es-palmerin`
    );
  }

  getValidateUserPalmerin(id: number): Observable<any> {
    return this.http.get(
      `${environment.backAPI}/users/${id}/es-palmerin-valido`
    );
  }

  penyaLogin(data: any): Observable<any> {
    return this.http.post(`${environment.backAPI}/signinpenya`, data);
  }

  onMaintenance(): Observable<boolean> {
    /*const now = Date.now();

    if (
      this.maintenanceCache.value !== null &&
      this.maintenanceCache.lastUpdated !== null &&
      now - this.maintenanceCache.lastUpdated < 60000
    ) {
      return of(this.maintenanceCache.value);
    }

    return this.http.get<boolean>(environment.backAPI + '/app/onMaintenance', httpOptions).pipe(
      tap((data) => {
        this.maintenanceCache.value = data;
        this.maintenanceCache.lastUpdated = now;
      })
    );*/
    return of(false);
  }

  /**
   * Estado AVET. Cache 60s + shareReplay para no repetir en cada navegación mobile.
   * Errores no se cachean.
   */
  avetStatus(): Observable<any> {
    const now = Date.now();
    if (
      this.avetStatusRequest$ &&
      this.avetStatusCachedAt !== null &&
      now - this.avetStatusCachedAt < this.HEALTH_CACHE_TTL_MS
    ) {
      return this.avetStatusRequest$;
    }

    this.avetStatusCachedAt = now;
    this.avetStatusRequest$ = this.http
      .get(environment.backAPI + '/app/avet-status', httpOptions)
      .pipe(
        tap({
          error: () => this.clearAvetStatusCache(),
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.avetStatusRequest$;
  }

procesosActivosStatus(): Observable<any> {
  return this.http.get(environment.backAPI + '/inscripcion/procesos-activos-status', httpOptions);
}

  /*saveFan(
    dob: string,
    email: string,
    password: string,
    name: string,
    surname1: string,
    surname2: string,
    docType: String,
    docNumber: String,
    country: string,
    phone: string,
    prefix: string,
    type: string,
    mktCommunications: number,
    mktProfile: number,
    mktThirdParty: number,
    legalCheck: number,
    lang: string

  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/fan/save-fan',
      {
        dob,
        email,
        password,
        name,
        surname1,
        surname2,
        docType,
        docNumber,
        country,
        phone,
        prefix,
        type,
        mktCommunications,
        mktProfile,
        mktThirdParty,
        legalCheck,

        lang,

      },
      httpOptions
    );
  }*/

  getProductPrice(type: string): Observable<number> {
    if (type === 'asbd') {
      // Devolvemos 0
      return of(0);
    }
    return this.http.get<number>(
      `${environment.backAPI}/product/get/price/${type}`,
      httpOptions
    );
  }

  getPriceByZoneAndGrada(zona: string, grada: string): Observable<number> {
    const zonaEncoded = encodeURIComponent(zona);
    const gradaEncoded = encodeURIComponent(grada);
    return this.http.get<number>(
      `${environment.backAPI}/abonado-flex/price/${zonaEncoded}/${gradaEncoded}`,
      httpOptions
    );
  }

  checkProductAvailable(type: string, id_betis: string): Observable<any> {
    return this.getAplicacionDisponible(type).pipe(
      switchMap((data) => {
        console.log("Respuesta de getAplicacionDisponible FAN:", type, data);
        if (data) {
          return of({ code: 1 });
        } else {
          return of({ code: 0 });
        }
      }),
      catchError((error) => {
        console.error("Error al comprobar getAplicacionDisponible pago FAN:", error);
        return of({ code: 0 });
      })
    );
  }

  checkProductPendingPay(type: string, id_betis: string): Observable<any> {
    return this.getAplicacionPendientePago(type).pipe(
      switchMap((data) => {
        console.log("Respuesta de getAplicacionPendientePago FAN:", type, data);
        if (data) {
          return of({ code: 1 });
        } else {
          return of({ code: 0 });
        }
      }),
      catchError((error) => {
        console.error("Error al comprobar getAplicacionPendientePago pago FAN:", error);
        return of({ code: 0 });
      })
    );
  }

  checkProductPendingValidation(type: string, id_betis: string): Observable<any> {
    return this.getAplicacionPendienteValidacion(type).pipe(
      switchMap((data) => {
        console.log("Respuesta de getAplicacionPendienteValidacion FAN:", type, data);
        if (data) {
          return of({ code: 1 });
        } else {
          return of({ code: 0 });
        }
      }),
      catchError((error) => {
        console.error("Error al comprobar getAplicacionPendienteValidacion pago FAN:", error);
        return of({ code: 0 });
      })
    );
  }

  checkAltaSoyPalerinRejected(): Observable<any> {
    return this.http.get(
      environment.backAPI + '/application/rejected/asp',
      httpOptions
    );
  }

  tpvUpdateInfo(
    applicationId: number,
    rules: number,
    wellcomePack: number,
    shippingType: string | null = null,
    shippingAddressDetail: any = null
  ): Observable<any> {
    return this.http.post(
      environment.backAPI + '/users/tpv/update-info',
      {
        applicationId: applicationId,
        rules: rules,
        wellcomePack: wellcomePack,
        shippingType,
        shippingAddressDetail
      },
      httpOptions
    );
  }

  getAplicacionDisponible(type: string): Observable<boolean> {
    return this.http.get<boolean>(environment.backAPI + '/application/available/' + type, httpOptions);
  }

  getAplicacionIdPendientePago(type: string): Observable<number> {
    return this.http.get<number>(environment.backAPI + '/application/pending/pay/id/' + type, httpOptions);
  }

  getAplicacionPendientePago(type: string): Observable<boolean> {
    return this.http.get<boolean>(environment.backAPI + '/application/pending/pay/' + type, httpOptions);
  }

  getAplicacionPendienteValidacion(type: string): Observable<boolean> {
    return this.http.get<boolean>(environment.backAPI + '/application/pending/validation/' + type, httpOptions);
  }

  lastPayment(tipo: string): Observable<LastPayment> {
    return this.getCurrentUserLocalStorage().pipe(
      switchMap((currentUser) => {
        if (!currentUser || !currentUser.id) {
          return throwError(new Error('No hay usuario o falta la ID'));
        }

        const lastPayment = `${environment.backAPI}/users/last-payment/${currentUser.id}/${tipo}`;
        return this.http.get<LastPayment>(lastPayment, httpOptions);
      })
    );
  }

  /**
   * Datos de abonado (POST /{id}/info). Cache por usuario + TTL:
   * evita repetir la petición más lenta del login si se vuelve a pedir en poco tiempo.
   * Errores no se cachean. Se invalida en login/logout.
   */
  public obtenerDatos(idUsuario: number): Observable<AppAbonado | null> {
    const ssoCookieContent = this.getSSOCookie('SSO-TEL');
    if (!ssoCookieContent) {
      return of(null);
    }

    const now = Date.now();
    const cached = this.infoCache.get(idUsuario);
    if (cached && now - cached.at < this.INFO_CACHE_TTL_MS) {
      return cached.request$;
    }

    const url = `${environment.backAPI}/${idUsuario}/info`;
    const request$ = this.http.post<AppAbonado>(url, {}).pipe(
      tap({
        error: () => this.infoCache.delete(idUsuario),
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.infoCache.set(idUsuario, { at: now, request$ });
    return request$;
  }

  public getInfoAbonado(idUsuario: number): Observable<AppAbonado> {
    return this.obtenerDatos(idUsuario).pipe(
      switchMap((data) => (data ? of(data) : throwError(() => new Error('Sin datos de abonado'))))
    );
  }

  getQr(id: number): Observable<AbonoQr> {
    if (this.getQrCache && Date.now() - this.getQrCache.at < this.GET_QR_TTL_MS) {
      return of(this.getQrCache.data);
    }
    const url = `${environment.backAPI}/qr`;
    return this.http.post<AbonoQr>(url, { id: id }, httpOptions).pipe(
      tap(response => this.getQrCache = { data: response, at: Date.now() }),
      shareReplay(1)
    );
  }

  /**
   * Health-check del servicio de socios.
   * Cache 60s + shareReplay: varias pantallas pueden llamar a la vez y solo sale 1 HTTP.
   * Los errores (p. ej. 401) no se cachean para no bloquear reintentos / logout.
   */
  status(): Observable<string> {
    const now = Date.now();
    if (
      this.statusRequest$ &&
      this.statusCachedAt !== null &&
      now - this.statusCachedAt < this.HEALTH_CACHE_TTL_MS
    ) {
      return this.statusRequest$;
    }

    const uri = `${environment.backAPI}/validate`;
    this.statusCachedAt = now;
    this.statusRequest$ = this.http.get(uri, { responseType: 'text' }).pipe(
      tap({
        error: () => this.clearStatusCache(),
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.statusRequest$;
  }

  validateFan(data: any) {
    console.log('Datos enviados para validación de fan:', data);
    let url = `${environment.backAPI}/fan/validate-data`;
    return this.http.post<any>(url, data);
    // Convertimos el objeto data en FormData
  }

  registerFan(registrationData: any) {
    return this.http.post(environment.backAPI + '/fan/register-fan', registrationData, httpOptions);
  }

  completeFan(formData: FormData) {
    return this.http.post(environment.backAPI + '/fan/update-fan', formData, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${environment.tokenAPI}`,
      }),
    });
  }

  isComplete(type: string): Observable<ResultResponse> {
    return this.http.get<ResultResponse>(
      environment.backAPI + `/fan/is-complete/${type}`,
      httpOptions
    );
  }

  deleteFan(idBetis: any) {
    // Send delete request to backend with idBetis
    return this.http.delete(`${environment.backAPI}/fan/delete-fan?idBetis=${idBetis}`, httpOptions);
  }

  tieneCamiseta() :Observable<CamisetaRenovacionResponse | null> {
    return this.http.get<CamisetaRenovacionResponse>(environment.backAPI + '/grupo-carnet/tiene-camiseta');
  }

  private maintenanceCache: { value: boolean | null; lastUpdated: number | null } = {
    value: null,
    lastUpdated: null
  };

  /** TTL compartido para /validate y /app/avet-status (navegación mobile). */
  private readonly HEALTH_CACHE_TTL_MS = 60_000;

  /** TTL para POST /{id}/info (petición más pesada del arranque mobile). */
  private readonly INFO_CACHE_TTL_MS = 60_000;

  private statusRequest$: Observable<string> | null = null;
  private statusCachedAt: number | null = null;

  private avetStatusRequest$: Observable<any> | null = null;
  private avetStatusCachedAt: number | null = null;

  private infoCache = new Map<
    number,
    { at: number; request$: Observable<AppAbonado | null> }
  >();

  private clearStatusCache(): void {
    this.statusRequest$ = null;
    this.statusCachedAt = null;
  }

  private clearAvetStatusCache(): void {
    this.avetStatusRequest$ = null;
    this.avetStatusCachedAt = null;
  }

  private clearInfoCache(): void {
    this.infoCache.clear();
  }

  /** Invalida caches de health/info al login/logout para no reutilizar estado de otra sesión. */
  private clearHealthCaches(): void {
    this.clearStatusCache();
    this.clearAvetStatusCache();
    this.clearInfoCache();
  }
}
